import { useEffect, useState } from "react";
import './style.scss';
import Helmet from "react-helmet";
import api from "../../api/Base";
import InstanceTarget from "../../types/instance/InstanceTarget";
import { EXPIRATION_KEY, messages, REDIRECT_PAGE, TOKEN_KEY } from "../../component/Constants";

function random(): number {
    return process.env.NODE_ENV === 'test' ? 0 : Math.random();
}

function Login(props: { loginError?: string }) {
    const [userName, setUserName] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [message, setMessage] = useState<string>(messages[Math.floor(random() * messages.length)]);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkingAuth, setCheckingAuth] = useState(true);

    const url = new URL(window.location.href);
    const errorParam = url.searchParams.get('error') || ''
    const [error, setError] = useState<string>(props.loginError || '');

    async function isAuthenticated(): Promise<boolean> {
        try {
            // Check if token exists and hasn't expired
            const token = window.localStorage.getItem(TOKEN_KEY);
            const expiration = window.localStorage.getItem(EXPIRATION_KEY);
            
            if (!token || !expiration) {
                return false;
            }
            
            if (new Date() > new Date(expiration)) {
                // Token expired, clean up
                window.localStorage.removeItem(TOKEN_KEY);
                window.localStorage.removeItem(EXPIRATION_KEY);
                return false;
            }

            // Verify token is still valid with server
            const url = process.env.REACT_APP_ARCHIVER + '/auth.jsp';
            await api<InstanceTarget[]>({ url, baseURL: '' });
            return true;
        } catch (error: any) {
            if (error.response?.status === 401) {
                // Clean up invalid token
                window.localStorage.removeItem(TOKEN_KEY);
                window.localStorage.removeItem(EXPIRATION_KEY);
                return false;
            }
            console.error('Unexpected auth error:', error);
            return false;
        }
    }

    useEffect(() => {
        async function checkLogin() {
            const loggedIn = await isAuthenticated();
            if (loggedIn) {
                // User is already authenticated, redirect to home or saved redirect URL
                const redirectUrl = window.localStorage.getItem(REDIRECT_PAGE) || '/';
                window.localStorage.removeItem(REDIRECT_PAGE); // Clean up redirect URL
                window.history.replaceState(null, '', redirectUrl);
                window.location.replace(redirectUrl);
                return;
            }
            setCheckingAuth(false);
        }

        // Run authentication check immediately
        checkLogin();
    }, []);

    useEffect(() => {
        if (errorParam) {
            setError(errorParam)
        }
    }, [errorParam]);

    useEffect(() => {

        let timeout: NodeJS.Timeout;

        timeout = setInterval(() => setMessage(messages[Math.floor(random() * messages.length)]), 9000);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const handleLogin = async () => {
        setLoading(true)
        // Prevent the Axios interceptor using the old token.
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(EXPIRATION_KEY);
        const token = btoa(`${userName}:${password}`);
        const url = process.env.REACT_APP_ARCHIVER + '/auth.jsp';
        api<InstanceTarget[]>({ url, baseURL: '', headers: { Authorization: 'Basic ' + token } })
                .then((response) => {
                    if (response && Array.isArray(response.data)) {
                        const redirectUrl = window.localStorage.getItem(REDIRECT_PAGE);
                        const expirationDate = new Date();
                        expirationDate.setMinutes(expirationDate.getMinutes() + 1);
                        window.localStorage.setItem(TOKEN_KEY, token);
                        window.localStorage.setItem(EXPIRATION_KEY, expirationDate.toISOString());
                        window.localStorage.removeItem(REDIRECT_PAGE);
                        api.post('login/name', [])
                                .then((/*response*/) => {
                                    window.history.replaceState(null, '', redirectUrl || '/');
                                    window.location.replace(redirectUrl || '/');
                                })
                                .catch((x: any) => {
                                    console.error('error', x);
                                    setLoading(false)
                                    setError('500');
                                });
                    } else {
                        console.error('error', response);
                        setLoading(false)
                        setError('500');
                    }
                })
                .catch((x: any) => {
                    let errorName = '401';

                    if (!x.response) {
                        console.error('error', x);
                        errorName = '500';
                    } else if (x.response.status !== 401) {
                        console.error('error', x);
                        errorName = x.response.statusText;
                    }

                    setLoading(false);
                    setError(errorName);
                });
    };

    const getErrorText = (value: string) => {
        const messages: { [key: string]: string } = {
            '401': 'Invalid Credentials',
            '500': '500 Internal Server Error',
            '502': '502 Bad Gateway',
            '503': '503 Service Unavailable',
            '504': '504 Gateway Timeout',
            expired: 'Session expired, please log in'
        };

        return messages[value] || '';
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            void handleLogin();
        }
    };
    if (checkingAuth) {
        return (
                <div className="dbmar-page-wrapper">
                    <div className="dbmar-page text-center">
                        <div className="spinner">
                            <img src="/icons/icon.png" className="fish" width="100" height="100" alt="Loading"/>
                        </div>
                        <p className="text-muted">Checking login status...</p>
                    </div>
                </div>
        );
    }

    return (
            <div className='dbmar-page-wrapper'>
                <div className='dbmar-page'>
                    <Helmet>
                        <title>DBmarlin Log In</title>
                        <meta name="description" content="Log In"/>
                    </Helmet>

                    <div className="card">
                        <div className="card-text-header">
                            <div className='text-center'>
                                <img src="/icons/dbmarlin-badge.svg" alt="DBmarlin" className="logo-large" width="200px"
                                     height="70px"/>
                            </div>
                            <p className='login-header'>Log In to DBmarlin</p>
                        </div>

                        <div className="col">
                            <div className='m-2'>
                                <input
                                        type="text"
                                        id="userName"
                                        placeholder='Username'
                                        className="form-control"
                                        onChange={(e) => setUserName(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                />
                            </div>

                            <div className='m-2 position-relative'>
                                <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        placeholder='Password'
                                        className="form-control"
                                        required={true}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                />
                                <i title={showPassword ? 'Hide password' : ' Show password'}
                                   className={`fa-solid show-password ${!showPassword ? 'fa-eye' : 'fa-eye-slash'}`}
                                   onClick={() => setShowPassword(!showPassword)}></i>
                            </div>
                            <p className='login-error'>{getErrorText(error)}</p>

                            <div className='m-2'>
                                <button onClick={() => handleLogin()} type='button'
                                        disabled={!userName?.trim() || !password?.trim() || loading}
                                        className={`btn btn-primary w-100`}>
                                    Login
                                </button>
                            </div>
                            <p className='login-link'><a
                                    href='https://docs.dbmarlin.com/docs/Getting-Started/access-control/authentication#update-user-change-password'>Lost
                                your password?</a></p>
                            <p className='login-link'><a
                                    href='https://docs.dbmarlin.com/docs/Getting-Started/access-control/authentication#add-user'>Add
                                new Account</a></p>
                        </div>
                    </div>

                    <div className="initialising page-animation">
                        <div className="w-100 text-center text-muted">
                            <div className="spinner"><img src="/icons/icon.png" className="fish" width="100"
                                                          height="100"
                                                          alt="DBmarlin"/></div>
                        </div>
                    </div>
                    <div className='invisible-wave'></div>
                    <div className="page-marlin-facts">
                        <div className="waves"></div>
                        <div className="message">{message}</div>
                        <p className="copyright">&#169;dbmarlin.com</p>
                    </div>
                </div>
            </div>
    )
}

export default Login;
